/**
 * Outbox v2 — what one send's result means for the entry (ATL-1445 stage 3, P3; plan §2 "How
 * failures are classified").
 *
 * The v1 dual-write outbox classifies the same errors, and this module REUSES its predicates rather
 * than re-deriving them (`dualWriteOutbox.ts`: dead backend, route vs entity 404, transport,
 * authorization; `dualWriteRefusal.ts`: status and code extraction across both error shapes). What
 * differs is the consequence, because a v2 entry is the ONLY copy of a master write:
 *
 *   v1 (a mirror of a write Firestore already holds)      v2 (the write itself)
 *   ─────────────────────────────────────────────────     ────────────────────────────────────────
 *   permanent 400/409/422 → DROPPED loudly                 → HELD: the queue stops, a person decides
 *   10 attempts / 7 days / size caps → DROPPED             → no cap of any kind; nothing is dropped
 *   backoff up to 6 h                                      → backoff capped at 5 min
 *
 * So there are exactly four outcomes, and none of them removes an entry except `delivered`:
 *
 *  - `delivered` — the backend has the write. A 2xx, or a 404 meaning "entity absent" on a DELETE
 *    (the thing the delete removes is not there; plan §2 closes the ATL-1193 asymmetry for v2) —
 *    and only when the 404 provably came from the backend (`isBackendEnvelope404` in `dualWriteOutbox.ts`, ATL-1199).
 *  - `retry` — the result says nothing about the payload: no response, a timeout, 408/429, a 5xx,
 *    401/403 (the CALLER was refused, ATL-908), a route 404 (not deployed yet, ATL-1194), the dead
 *    Railway host (ATL-902), a 404 the backend did not produce, or an error with no HTTP shape.
 *  - `held` — the backend evaluated this payload and refused it deterministically: 400, 409, 422, an
 *    entity 404 on anything but a delete, any other 4xx, the client's own request validation, or a
 *    200 from a per-item-result route (`outboxPerItemResults.ts`: stock/bulk, cost-cascade/bulk, …)
 *    whose rows report a failure or are missing.
 *  - `master-changed` — the refusal says the write master is not what this device thinks
 *    (`write_master_supabase`, `write_master_firestore`, any case, any status). Nothing is decided
 *    about the entry; the drain stops and the flag is re-read (plan §4, which P5 wires).
 *    `write_master_undetermined` is NOT this (review 1 S5): it means a plane was UNREADABLE — the
 *    backend's 503 — which says nothing about which master is right. It is retried with backoff.
 *
 * A code is read from the body, never from the message (`refusalCode`), so renaming a message cannot
 * reclassify a refusal. An unknown shape is `retry`, not `held`: holding says "a person must look at
 * this payload", which an error that never reached a server cannot justify, and retrying costs one
 * request per five minutes.
 */
import {
  isAuthorizationFailure,
  isDeadBackendFailure,
  isEntityAbsentFailure,
  isRouteNotFoundFailure,
  isTransportFailure,
} from '../Firebase/dualWriteOutbox'
import { failureStatus, refusalCode, UNCODED_REFUSAL } from '../Firebase/dualWriteRefusal'
import type { OutboxEntryV2, OutboxRequest } from './outboxEntry'
import { perItemFailures, perItemResultRouteOf } from './outboxPerItemResults'

export type RetryReason =
  | 'transport'
  | 'timeout'
  | 'rate-limited'
  | 'server-error'
  | 'unauthorized'
  | 'forbidden'
  | 'route-not-found'
  | 'dead-backend'
  /** A 404 that did not come from the backend's own exception filter (a CDN, a proxy): ATL-1199. */
  | 'unrecognised-404'
  /** `write_master_undetermined`: the backend could not read a write-master plane (review 1 S5). */
  | 'master-undetermined'
  | 'unknown'

export type HeldReason =
  | 'rejected'
  | 'refused'
  | 'not-found'
  | 'client-error'
  | 'client-validation'
  | 'partial-failure'
  /** A per-item-result route answered 200 without its `results` rows: which items landed is unknown. */
  | 'unverifiable'
  /** Set by the drainer, never by a send: the entry was queued by another install (P2 obligation 2). */
  | 'foreign-install'
  /**
   * Set by the drainer, never by a send: an ABSOLUTE stock mirror that was not written by this
   * session's live writer (a migrated v1 entry, a converted 409). Replaying an absolute level onto the
   * ledger after the fact is the ATL-707 class in reverse (plan §2); it is never sent automatically.
   */
  | 'absolute-replay'

export type DeliveryOutcome =
  | { kind: 'delivered'; detail: 'ok' | 'entity-absent-delete' }
  | { kind: 'retry'; reason: RetryReason; status: number | null; code: string | null }
  | { kind: 'held'; reason: HeldReason; status: number | null; code: string | null }
  | { kind: 'master-changed'; status: number | null; code: string }

/** Backend codes that say "the write master is not what this device thinks" (lower-cased). */
export const WRITE_MASTER_CODES: ReadonlySet<string> = new Set(['write_master_supabase', 'write_master_firestore'])

/** An unreadable write-master plane: transient, retried with backoff, never a pause (review 1 S5). */
export const WRITE_MASTER_UNDETERMINED = 'write_master_undetermined'

/**
 * Requests that set an ABSOLUTE stock level: the bulk mirror and the single-product manual set.
 * Pinned here rather than inferred from a label, because the label is also worn by older shapes.
 */
export function isAbsoluteStockRequest(request: Pick<OutboxRequest, 'method' | 'url'>): boolean {
  if (request.method === 'POST' && request.url === '/products/stock/bulk') return true
  return request.method === 'PATCH' && /^\/products\/[^/]+\/stock$/.test(request.url)
}

/**
 * A hold only an operator may lift (review 1 S4, narrowed in review 2): an absolute STOCK request.
 * The 30-minute retry, `app-start`, `deploy` and `manual` never resend it.
 *
 * Why stock only, decided deliberately (stage-3 doc §13): a stock level is a projection of the movement ledger,
 * and re-asserting a level after the fact desynchronises the cache from the ledger in a way no later
 * write repairs (ATL-707, in reverse) — plan §2 forbids it outright. The other `kind: 'absolute'`
 * writes (a shift's closing amounts, a client edit) are field sets on one document: retrying a refused
 * one is a LATE first delivery of the same value — exactly what v1's outbox does for them today — and
 * stage 2e makes the shift amounts server-computed anyway. Holding them operator-only would turn every
 * deterministic refusal of a shift close into a stop only an operator can lift, for no protection.
 */
export function isOperatorOnlyHold(entry: Pick<OutboxEntryV2, 'request'>): boolean {
  return isAbsoluteStockRequest(entry.request)
}

/** The code this refusal carries, or `null` (never the `uncoded` sentinel: that is not a code). */
function codeOf(error: unknown): string | null {
  const code = refusalCode(error)
  return code === UNCODED_REFUSAL ? null : code
}

/** Client-side Zod refusal of the REQUEST body (`ApiValidationError`, type `request`). */
function isClientRequestValidation(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { name?: unknown; type?: unknown }
  return e.name === 'ApiValidationError' && e.type === 'request'
}

function isTimeout(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: unknown; message?: unknown; response?: unknown }
  if (e.response !== undefined) return false
  return e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || /timeout/i.test(String(e.message ?? ''))
}

/** A send that resolved: 2xx. Only a per-item failure report turns it into anything else. */
export function classifySuccess(entry: Pick<OutboxEntryV2, 'request'>, response: unknown): DeliveryOutcome {
  const route = perItemResultRouteOf(entry.request)
  if (route) {
    const failures = perItemFailures(route, response)
    if (failures === 'unverifiable') return { kind: 'held', reason: 'unverifiable', status: 200, code: null }
    if (failures !== null) return { kind: 'held', reason: 'partial-failure', status: 200, code: null }
  }
  return { kind: 'delivered', detail: 'ok' }
}

/** A send that rejected. */
export function classifyFailure(entry: Pick<OutboxEntryV2, 'kind'>, error: unknown): DeliveryOutcome {
  const status = failureStatus(error) ?? null
  const code = codeOf(error)

  if (code !== null && WRITE_MASTER_CODES.has(code.toLowerCase())) {
    return { kind: 'master-changed', status, code }
  }
  if (code !== null && code.toLowerCase() === WRITE_MASTER_UNDETERMINED) {
    return { kind: 'retry', reason: 'master-undetermined', status, code }
  }
  if (isClientRequestValidation(error)) {
    return { kind: 'held', reason: 'client-validation', status: null, code: null }
  }
  if (status === null) {
    if (isTimeout(error)) return { kind: 'retry', reason: 'timeout', status: null, code: null }
    if (isTransportFailure(error)) return { kind: 'retry', reason: 'transport', status: null, code: null }
    return { kind: 'retry', reason: 'unknown', status: null, code: null }
  }
  if (status >= 200 && status < 300) {
    // A resolved status arriving as a rejection is not something the transport does; do not guess.
    return { kind: 'retry', reason: 'unknown', status, code }
  }
  if (status === 404) {
    if (isDeadBackendFailure(error)) return { kind: 'retry', reason: 'dead-backend', status, code }
    if (isRouteNotFoundFailure(error)) return { kind: 'retry', reason: 'route-not-found', status, code }
    // ATL-1199: `isEntityAbsentFailure` requires the backend's envelope (`isBackendEnvelope404`).
    if (!isEntityAbsentFailure(error)) {
      return { kind: 'retry', reason: 'unrecognised-404', status, code }
    }
    if (entry.kind === 'delete') return { kind: 'delivered', detail: 'entity-absent-delete' }
    return { kind: 'held', reason: 'not-found', status, code }
  }
  if (isAuthorizationFailure(error)) {
    return { kind: 'retry', reason: status === 403 ? 'forbidden' : 'unauthorized', status, code }
  }
  if (status === 408) return { kind: 'retry', reason: 'timeout', status, code }
  if (status === 429) return { kind: 'retry', reason: 'rate-limited', status, code }
  if (status >= 500) return { kind: 'retry', reason: 'server-error', status, code }
  if (status === 400) return { kind: 'held', reason: 'rejected', status, code }
  if (status === 409 || status === 422) return { kind: 'held', reason: 'refused', status, code }
  if (status >= 400) return { kind: 'held', reason: 'client-error', status, code }
  // 1xx/3xx surfaced as a failure: nothing about the payload; retry.
  return { kind: 'retry', reason: 'unknown', status, code }
}
